import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/api';
import '../App.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Control Chart constants for n=10 (from standard X-Bar R chart tables)
// A2 = 0.308, D3 = 0.223, D4 = 1.777
// These are used to calculate the control limits.
const CONTROL_CHART_CONSTANTS = {
  10: { A2: 0.308, D3: 0.223, D4: 1.777 }
};

function Dashboard() {
  const navigate = useNavigate();
  const [allData, setAllData] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedParameter, setSelectedParameter] = useState('');

  const [uniqueDates, setUniqueDates] = useState([]);
  const [uniqueParameters, setUniqueParameters] = useState([]);

  const handleLogout = () => {
    sessionStorage.removeItem('userName');
    sessionStorage.removeItem('databaseKey');
    navigate('/login');
  };

  // Fetch data on mount
  useEffect(() => {
    const userName = sessionStorage.getItem('userName');
    if (userName) {
      api.getInspectionDatas(userName)
        .then(data => {
          if (Array.isArray(data)) {
            setAllData(data);

            const dates = [...new Set(data.map(item => item.date))].sort();
            const parameters = [...new Set(data.map(item => item.parameter))];
            setUniqueDates(dates);
            setUniqueParameters(parameters);

            if (dates.length > 0) {
              setStartDate(dates[0]); // Default to the earliest date
              setEndDate(dates[dates.length - 1]); // Default to the latest date
            }
            if (parameters.length > 0) {
              setSelectedParameter(parameters[0]);
            }
          } else {
            console.error("Error: Fetched data is not an array.", data);
            setAllData([]);
          }
        })
        .catch(error => {
          console.error("Error loading data from API:", error);
          setAllData([]);
        });
    }
  }, []);

  // --- NEW: Create filtered date options for the dropdowns ---
  const availableStartDates = useMemo(() => {
    if (!endDate) return uniqueDates; // If no end date, show all
    // Show only dates that are less than or equal to the current end date
    return uniqueDates.filter(date => date <= endDate);
  }, [uniqueDates, endDate]);

  const availableEndDates = useMemo(() => {
    if (!startDate) return uniqueDates; // If no start date, show all
    // Show only dates that are greater than or equal to the current start date
    return uniqueDates.filter(date => date >= startDate);
  }, [uniqueDates, startDate]);

  // --- NEW: Add effect to auto-correct date range ---
  // If startDate changes and is now AFTER endDate, set endDate to startDate
  useEffect(() => {
    if (startDate && endDate && startDate > endDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  // If endDate changes and is now BEFORE startDate, set startDate to endDate
  useEffect(() => {
    if (startDate && endDate && endDate < startDate) {
      setStartDate(endDate);
    }
  }, [endDate, startDate]);


  // Filter data based on user selections
  const filteredData = useMemo(() => {
    if (!Array.isArray(allData) || !startDate || !endDate) {
      return [];
    }
    // Filter by date range
    return allData
      .filter(item => 
        item.date >= startDate &&
        item.date <= endDate &&
        item.parameter === selectedParameter
      )
      .sort((a, b) => a.file.localeCompare(b.file));
  }, [allData, startDate, endDate, selectedParameter]); // Updated dependencies

  // Calculate chart data
  const chartData = useMemo(() => {
    if (filteredData.length === 0) {
      return { xBar: null, rChart: null, stats: null };
    }

    const labels = filteredData.map((d, i) => `Sample ${i + 1}`);
    const validXBarValues = filteredData.map(d => d.x_bar).filter(val => val !== null && val !== undefined);
    const validRValues = filteredData.map(d => d.r_value).filter(val => val !== null && val !== undefined);

    if (validXBarValues.length === 0 || validRValues.length === 0) {
      return { xBar: null, rChart: null, stats: null };
    }
    
    const firstItem = filteredData[0];
    const LSL = firstItem.lower_limit;
    const USL = firstItem.upper_limit;

    const X_double_bar = validXBarValues.reduce((acc, val) => acc + val, 0) / validXBarValues.length;
    const R_bar = validRValues.reduce((acc, val) => acc + val, 0) / validRValues.length;

    const n = 10;
    const consts = CONTROL_CHART_CONSTANTS[n];

    if (!consts) {
      console.error(`Control chart constants for n=${n} not found.`);
      return { xBar: null, rChart: null, stats: null };
    }

    const UCL_X = X_double_bar + (consts.A2 * R_bar);
    const CL_X = X_double_bar;
    const LCL_X = X_double_bar - (consts.A2 * R_bar);

    const UCL_R = consts.D4 * R_bar;
    const CL_R = R_bar;
    const LCL_R = consts.D3 * R_bar;
    
    const stats = {
      X_double_bar: X_double_bar.toFixed(4),
      R_bar: R_bar.toFixed(4),
      LSL,
      USL,
      UCL_X: UCL_X.toFixed(4),
      CL_X: CL_X.toFixed(4),
      LCL_X: LCL_X.toFixed(4),
      UCL_R: UCL_R.toFixed(4),
      CL_R: CL_R.toFixed(4),
      LCL_R: LCL_R.toFixed(4),
    };

    const xBarChartData = {
      labels,
      datasets: [
        {
          label: 'X-bar (Sample Mean)',
          data: filteredData.map(d => d.x_bar),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1,
          spanGaps: true,
        },
        {
          label: 'UCL (X-bar)',
          data: Array(labels.length).fill(UCL_X),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'CL (X-bar)',
          data: Array(labels.length).fill(CL_X),
          borderColor: 'rgb(54, 162, 235)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'LCL (X-bar)',
          data: Array(labels.length).fill(LCL_X),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        ...(LSL !== null ? [{
          label: 'LSL',
          data: Array(labels.length).fill(LSL),
          borderColor: 'rgb(255, 159, 64)',
          borderDash: [10, 10],
          pointRadius: 0,
          fill: false
        }] : []),
        ...(USL !== null ? [{
          label: 'USL',
          data: Array(labels.length).fill(USL),
          borderColor: 'rgb(255, 159, 64)',
          borderDash: [10, 10],
          pointRadius: 0,
          fill: false
        }] : []),
      ]
    };

    const rChartData = {
      labels,
      datasets: [
        {
          label: 'R (Sample Range)',
          data: filteredData.map(d => d.r_value),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.1,
          spanGaps: true,
        },
        {
          label: 'UCL (R)',
          data: Array(labels.length).fill(UCL_R),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'CL (R)',
          data: Array(labels.length).fill(CL_R),
          borderColor: 'rgb(54, 162, 235)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        },
        {
          label: 'LCL (R)',
          data: Array(labels.length).fill(LCL_R),
          borderColor: 'rgb(255, 99, 132)',
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false
        }
      ]
    };
    
    const chartTitle = `${selectedParameter} (${startDate} to ${endDate})`;
    
    const xBarOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: `X-Bar Chart for ${chartTitle}` }
      }
    };
    
    const rChartOptions = {
      responsive: true,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: `R Chart for ${chartTitle}` }
      }
    };

    return { xBar: { data: xBarChartData, options: xBarOptions }, rChart: { data: rChartData, options: rChartOptions }, stats };

  }, [filteredData, selectedParameter, startDate, endDate]);

  return (
    <div className="App">
      <header className="App-header">
        <h1>Inspection Quality Dashboard</h1>
        <nav className="menu-bar">
          <Link to="/dashboard" className="menu-link">Dashboard</Link>
          <Link to="/gipform" className="menu-link">GIP Form</Link>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </nav>
      </header>
      
      <main>
        <div className="filters">
          
          <div className="filter-item">
            <label htmlFor="start-date-select">From Date:</label>
            <select 
              id="start-date-select"
              value={startDate} 
              onChange={e => setStartDate(e.target.value)}
            >
              {/* --- CHANGE: Use availableStartDates --- */}
              {availableStartDates.map(date => (
                <option key={`start-${date}`} value={date}>{date}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="end-date-select">To Date:</label>
            <select 
              id="end-date-select"
              value={endDate} 
              onChange={e => setEndDate(e.target.value)}
            >
              {/* --- CHANGE: Use availableEndDates --- */}
              {availableEndDates.map(date => (
                <option key={`end-${date}`} value={date}>{date}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-item">
            <label htmlFor="param-select">Select Parameter:</label>
            <select 
              id="param-select"
              value={selectedParameter}
              onChange={e => setSelectedParameter(e.target.value)}
            >
              {uniqueParameters.map(param => (
                <option key={param} value={param}>{param}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredData.length > 0 && chartData.stats ? (
          <div className="content-wrapper">
            <div className="stats-summary">
              <h2>Summary Statistics</h2>
              <p><strong>Parameter:</strong> {selectedParameter}</p>
              <p><strong>Date Range:</strong> {startDate} to {endDate}</p>
              <p><strong>Number of Samples:</strong> {filteredData.length}</p>
              <p><strong>Specification Limit (LSL):</strong> {chartData.stats.LSL ?? 'N/A'}</p>
              <p><strong>Specification Limit (USL):</strong> {chartData.stats.USL ?? 'N/A'}</p>
              <hr />
              <h3>X-Bar Chart Stats</h3>
              <p><strong>X-double-bar (CL):</strong> {chartData.stats.CL_X}</p>
              <p><strong>UCL (X-bar):</strong> {chartData.stats.UCL_X}</p>
              <p><strong>LCL (X-bar):</strong> {chartData.stats.LCL_X}</p>
              <hr />
              <h3>R Chart Stats</h3>
              <p><strong>R-bar (CL):</strong> {chartData.stats.CL_R}</p>
              <p><strong>UCL (R):</strong> {chartData.stats.UCL_R}</p>
              <p><strong>LCL (R):</strong> {chartData.stats.LCL_R}</p>
            </div>

            <div className="charts-container">
              {chartData.xBar && (
                <div className="chart">
                  <Line options={chartData.xBar.options} data={chartData.xBar.data} />
                </div>
              )}
              {chartData.rChart && (
                <div className="chart">
                  <Line options={chartData.rChart.options} data={chartData.rChart.data} />
                </div>
              )}
            </div>

            <div className="data-table-container">
              <h2>Sample Data (Deviation)</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Sample (File)</th>
                    <th>X-bar (X̄)</th>
                    <th>Range (R)</th>
                    <th>Observations (n=10)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item, index) => (
                    <tr key={item.file || index}>
                      <td>Sample {index + 1} ({item.file?.split('-')[2]?.split('.')[0] || 'File'})</td>
                      <td>{item.x_bar?.toFixed(4)}</td>
                      <td>{item.r_value?.toFixed(4)}</td>
                      <td>{item.observations.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p>Loading data or no data found for the selected filters. Check browser console for errors.</p>
        )}
      </main>
    </div>
  );
}

export default Dashboard;